import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ESRI_GEOCODE_SERVICE_URL } from '@rsos/maps/interface/reverseGeocode/helpers';

/**
 * Reverse geocode coordinates to addresses on the Esri map using the specified geocode service.
 * The service returns an address if an address is found, or an error if it cannot find a result.
 * https://developers.arcgis.com/javascript/latest/reverse-geocode/
 * @param {String} serviceUrl - A GIS or Esri geocode service url
 * @param {String} searchTerm - input value in ConnectedUniversalSearch
 * @param {Number} latitude - The location's latitude
 * @param {Number} longitude - The location's longitude
 *
 */

const isValidedCandidates = result => {
  if (
    result?.data?.error?.code > 299 ||
    result?.data?.error?.code < 200 ||
    !result?.data?.candidates?.length
  ) {
    return false;
  }
  return true;
};

const staggeredBaseQuery = async (args, api, extraOptions) => {
  if (!args?.params?.address) return { error: 'Address not provided' };
  let result;
  if (args.extraOptions.serviceUrl) {
    result = await fetchBaseQuery({
      baseUrl: args.extraOptions.serviceUrl,
    })(args, api, extraOptions);
  }

  if (isValidedCandidates(result)) {
    return result;
  } else {
    delete args.params.token;
    result = await fetchBaseQuery({
      baseUrl: ESRI_GEOCODE_SERVICE_URL,
    })(args, api, extraOptions);
  }
  return result;
};

export const getSuggestedAddressesApi = createApi({
  reducerPath: 'getSuggestedAddressesApi',
  baseQuery: staggeredBaseQuery,

  endpoints: builder => ({
    getSuggestedAddresses: builder.query({
      query: ({
        searchTerm,
        latitude,
        longitude,
        token,
        serviceUrl,
        countryCode = 'USA',
      }) => {
        return {
          url: `/findAddressCandidates`,
          method: 'GET',
          params: {
            token,
            address: searchTerm,
            countryCode: countryCode,
            location: `${longitude}, ${latitude}`,
            maxLocations: 10,
            outFields: '*',
            f: 'json', // ESRI API requires response format as JSON
          },
          extraOptions: { token, serviceUrl },
        };
      },
      transformResponse: resp => resp?.candidates || [],
      keepUnusedDataFor: 3600, //1 hour
    }),
  }),
});

export const { useGetSuggestedAddressesQuery } = getSuggestedAddressesApi;
export default getSuggestedAddressesApi.endpoints.getSuggestedAddresses;
